import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { api } from '../hooks/useAuth';
import usePermissions from '../hooks/usePermissions';
import Swal from 'sweetalert2';

const customerSchema = z.object({
  name: z.string().min(1, 'validation.required'),
  email: z.string().email('validation.email').min(1, 'validation.required'),
  address: z.string().min(1, 'validation.required'),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  phone: z.string().optional(),
  taxNumber: z.string().optional(),
  tradeRegisterNumber: z.string().optional(),
});

const Customers = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch customers
  const { data: customers, isLoading } = useQuery({
    queryKey: ['customers', searchQuery],
    queryFn: async () => {
      const response = await api.get(`/customers${searchQuery ? `?query=${searchQuery}` : ''}`);
      return response.data;
    },
  });

  // Create customer mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/customers', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setShowForm(false);
      setEditingCustomer(null);
      reset({
        name: '',
        email: '',
        address: '',
        postalCode: '',
        city: '',
        phone: '',
        taxNumber: '',
        tradeRegisterNumber: '',
      });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('customers.createdSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('Customer creation error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('customers.creationFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  // Update customer mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/customers/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setShowForm(false);
      setEditingCustomer(null);
      reset({
        name: '',
        email: '',
        address: '',
        postalCode: '',
        city: '',
        phone: '',
        taxNumber: '',
        tradeRegisterNumber: '',
      });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('customers.updatedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('Customer update error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('customers.updateFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  // Delete customer mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/customers/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('customers.deletedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('Customer deletion error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('customers.deletionFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: '',
      email: '',
      address: '',
      postalCode: '',
      city: '',
      phone: '',
      taxNumber: '',
      tradeRegisterNumber: '',
    },
  });

  const onSubmit = (data) => {
    if (editingCustomer) {
      if (!canUpdate('customers')) {
        Swal.fire({
          icon: 'error',
          title: t('common.error'),
          text: t('permissions.insufficientPermissions'),
          confirmButtonText: t('common.ok')
        });
        return;
      }
      updateMutation.mutate({ id: editingCustomer._id, data });
    } else {
      if (!canCreate('customers')) {
        Swal.fire({
          icon: 'error',
          title: t('common.error'),
          text: t('permissions.insufficientPermissions'),
          confirmButtonText: t('common.ok')
        });
        return;
      }
      createMutation.mutate(data);
    }
  };

  const handleEdit = (customer) => {
    if (!canUpdate('customers')) {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: t('permissions.insufficientPermissions'),
        confirmButtonText: t('common.ok')
      });
      return;
    }
    setEditingCustomer(customer);
    reset(customer);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCustomer(null);
    reset({
      name: '',
      email: '',
      address: '',
      postalCode: '',
      city: '',
      phone: '',
      taxNumber: '',
      tradeRegisterNumber: '',
    });
  };

  const handleDelete = (customer) => {
    if (!canDelete('customers')) {
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: t('permissions.insufficientPermissions'),
        confirmButtonText: t('common.ok')
      });
      return;
    }
    
    Swal.fire({
      title: t('customers.confirmDelete'),
      text: t('customers.confirmDeleteText', { name: customer.name }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('customers.yesDelete'),
      cancelButtonText: t('common.cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMutation.mutate(customer._id);
      }
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('customers.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('customers.description')}
          </p>
        </div>
        <button
          onClick={() => {
            setEditingCustomer(null);
            reset({
              name: '',
              email: '',
              address: '',
              postalCode: '',
              city: '',
              phone: '',
              taxNumber: '',
              tradeRegisterNumber: '',
            });
            setShowForm(true);
          }}
          className="btn btn-primary"
        >
          {t('customers.createCustomer')}
        </button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Modal */}
      {showForm && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={handleCancel}
        >
          <div 
            className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingCustomer ? t('customers.editCustomer') : t('customers.createCustomer')}
                </h3>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label">{t('customers.customerName')} *</label>
                    <input
                      {...register('name')}
                      className={`input ${errors.name ? 'input-error' : ''}`}
                      placeholder={t('customers.customerName')}
                    />
                    {errors.name && (
                      <p className="error-message">{t(errors.name.message)}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">{t('customers.email')} *</label>
                    <input
                      {...register('email')}
                      type="email"
                      className={`input ${errors.email ? 'input-error' : ''}`}
                      placeholder={t('customers.email')}
                    />
                    {errors.email && (
                      <p className="error-message">{t(errors.email.message)}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="label">{t('customers.address')} *</label>
                  <textarea
                    {...register('address')}
                    className={`input ${errors.address ? 'input-error' : ''}`}
                    rows={3}
                    placeholder={t('customers.address')}
                  />
                  {errors.address && (
                    <p className="error-message">{t(errors.address.message)}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label">{t('customers.postalCode')}</label>
                    <input
                      {...register('postalCode')}
                      className="input"
                      placeholder={t('customers.postalCode')}
                    />
                  </div>
                  <div>
                    <label className="label">{t('customers.city')}</label>
                    <input
                      {...register('city')}
                      className="input"
                      placeholder={t('customers.city')}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label">{t('customers.phone')}</label>
                    <input
                      {...register('phone')}
                      className="input"
                      placeholder={t('customers.phone')}
                    />
                  </div>
                  <div>
                    <label className="label">{t('customers.taxNumber')}</label>
                    <input
                      {...register('taxNumber')}
                      className="input"
                      placeholder={t('customers.taxNumber')}
                    />
                  </div>
                </div>
                <div>
                  <label className="label">{t('customers.tradeRegisterNumber')}</label>
                  <input
                    {...register('tradeRegisterNumber')}
                    className="input"
                    placeholder={t('customers.tradeRegisterNumber')}
                  />
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="btn btn-primary"
                  >
                    {createMutation.isPending || updateMutation.isPending
                      ? t('common.loading')
                      : t('common.save')}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn btn-secondary"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Customers List */}
      <div className="card">
        {customers && customers.length > 0 ? (
          <div className="overflow-hidden">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('customers.customerName')}</th>
                  <th>{t('customers.email')}</th>
                  <th>{t('customers.phone')}</th>
                  <th>{t('customers.address')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer._id}>
                    <td className="font-medium">{customer.name}</td>
                    <td>{customer.email || '-'}</td>
                    <td>{customer.phone || '-'}</td>
                    <td>{customer.address || '-'}</td>
                    <td>
                      <div className="flex space-x-2">
                        {canUpdate('customers') && (
                          <button
                            onClick={() => handleEdit(customer)}
                            className="text-primary-600 hover:text-primary-500 text-sm"
                          >
                            {t('common.edit')}
                          </button>
                        )}
                        {canDelete('customers') && (
                          <button
                            onClick={() => handleDelete(customer)}
                            className="text-red-600 hover:text-red-500 text-sm"
                          >
                            {t('common.delete')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('customers.noCustomers')}</h3>
            <p className="text-gray-500 mb-4">{t('customers.createFirst')}</p>
            <button
              onClick={() => {
                if (!canCreate('customers')) {
                  Swal.fire({
                    icon: 'error',
                    title: t('common.error'),
                    text: t('permissions.insufficientPermissions'),
                    confirmButtonText: t('common.ok')
                  });
                  return;
                }
                setShowForm(true);
              }}
              className="btn btn-primary"
            >
              {t('customers.createCustomer')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;
